import dayjs from 'dayjs';
import { isBigger, isInRange } from '../../../helper/ResultHelper';
import { resolveUnitByObis } from '../../../helper/MeterHelper';
import { ApplicationConfig, DefaultJobComment } from '../../../redux/application/application.types';
import { MeterRateTaskType } from '../../../redux/jobs/jobs.types';
import { AccumulatorState } from './registerAccumulator';

// Plausibility enforcement for the assistant's confirm step — a MIRROR of the manual flow in
// JobDetails (resultReducer + the send-button gating around JobDetails.tsx:502-593). The two
// capture paths must behave identically: the same job/config flags decide whether an implausible
// value merely warns, requires a reading hint (comment), requires an image, or blocks submission
// outright (strictValidation). Deliberate deviations from JobDetails, both assistant-specific:
//  - post-decimal validation is omitted: the confirm step's MeterReadingInput enforces the
//    pre/post-decimal structure at input time, so a mismatch cannot be entered at all.
//  - empty values produce no historic/continuous/range errors (JobDetails marks a cleared input
//    as historic-invalid via isBigger(NaN, …)); the assistant never submits empty registers, and
//    strict mode covers them via strictInvalid.

export interface ConfirmValidationFeatures {
    // Job-based: any task with readingValueValidation.strictValidation makes the whole job strict
    // (same aggregation as JobDetails.tsx:728) — range violations then BLOCK submission.
    strictValidation: boolean;
    imageAlwaysRequired: boolean;
    unplausibleReadingsImageRequired: boolean;
    unplausibleReadingsCommentRequired: boolean;
    historicReadingsValidationRequired: boolean;
    continuousReadingsValidationRequired: boolean;
    commentsEnabled: boolean;
    freeTextCommentEnabled: boolean;
    defaultComments?: DefaultJobComment[];
}

// Same flag resolution as JobDetails.tsx:341-360: comment requirement only exists with comments
// enabled at all, image requirements are void when images are forbidden. The jobConfig passed in
// must already be job-merged (mergeJobConfig(job.config, applicationConfig)) so per-job overrides
// win over the tenant-wide configuration — the flags are job-based, not only global.
export const resolveConfirmValidationFeatures = (
    jobConfig: ApplicationConfig | undefined,
    tasks: MeterRateTaskType[],
): ConfirmValidationFeatures => {
    const features = jobConfig?.features;
    const commentsEnabled = jobConfig?.meterReading?.commentsEnabled ?? false;
    const imagesForbidden = features?.imagesForbidden ?? false;
    return {
        strictValidation: tasks.reduce((res, mr) => res || mr.readingValueValidation?.strictValidation || false, false),
        imageAlwaysRequired: imagesForbidden ? false : features?.imageAlwaysRequired ?? false,
        unplausibleReadingsImageRequired: imagesForbidden ? false : features?.unplausibleReadingsImageRequired ?? false,
        unplausibleReadingsCommentRequired: !commentsEnabled ? false : features?.unplausibleReadingsCommentRequired ?? false,
        historicReadingsValidationRequired: features?.historicReadingsValidationRequired ?? false,
        continuousReadingsValidationRequired: features?.continuousReadingsValidationRequired ?? false,
        commentsEnabled,
        freeTextCommentEnabled: !commentsEnabled ? false : jobConfig?.meterReading?.freeTextCommentEnabled ?? true,
        defaultComments: jobConfig?.meterReading?.defaultComments,
    };
};

export interface ConfirmTaskValidation {
    isEmpty: boolean;
    rangeError: boolean;
    // Historic/continuous errors are pre-gated on their feature flag AND the presence of a
    // comparison value, exactly like the consumers in JobDetails (1172-1179) expect them.
    historicError: boolean;
    continuousError: boolean;
    hasAnyValidationError: boolean;
    // In strict mode every register needs a filled, in-range value (JobDetails: valid=false for
    // empty or out-of-range values, and the send button needs ALL values valid).
    strictInvalid: boolean;
    isFeedInMeter: boolean;
    // {{historicReadingValue}}/{{unit}}/{{historicReadingDate}} for the warning texts — same
    // interpolation as JobDetails.tsx:1194-1199.
    interpolationParams: { historicReadingValue?: number; unit?: string; historicReadingDate?: string };
}

const latestByReadingDate = <T extends { readingDate?: string }>(items?: T[]): T | undefined =>
    !items || items.length === 0
        ? undefined
        : items.reduce((prev, current) => (dayjs(prev.readingDate).valueOf() > dayjs(current.readingDate).valueOf() ? prev : current));

export const validateConfirmTask = (task: MeterRateTaskType, value: string, features: ConfirmValidationFeatures): ConfirmTaskValidation => {
    const isEmpty = value === '';
    const numeric = Number.parseFloat(value);
    const postDecimals = task.postDecimals ?? 0;
    const lastReadingEvent = latestByReadingDate(task.readingEvents);
    const lastReadingResult = latestByReadingDate(task.readingResults);

    // Mirrors resultReducer 'setResult' (JobDetails.tsx:200-204) including the min/max fallbacks
    // and the "no validation configured means everything is plausible" rule.
    const rangeError =
        !isEmpty &&
        !(
            isInRange(
                numeric,
                task.readingValueValidation?.minimumValue || 0,
                task.readingValueValidation?.maximumValue || Number.MAX_SAFE_INTEGER,
                postDecimals,
            ) || task.readingValueValidation == null
        );
    const historicError =
        !isEmpty &&
        features.historicReadingsValidationRequired &&
        lastReadingEvent?.readingValue != null &&
        !isBigger(numeric, lastReadingEvent.readingValue, postDecimals);
    const continuousError =
        !isEmpty &&
        features.continuousReadingsValidationRequired &&
        lastReadingResult?.readingValue != null &&
        !isBigger(numeric, lastReadingResult.readingValue, postDecimals);

    const unit = task.unit || resolveUnitByObis(task.obis);
    return {
        isEmpty,
        rangeError,
        historicError,
        continuousError,
        hasAnyValidationError: rangeError || historicError || continuousError,
        strictInvalid: isEmpty || rangeError,
        // Same feed-in meter heuristic as JobDetails.tsx:1191.
        isFeedInMeter: task.obis?.includes(':2.8') ?? false,
        interpolationParams: {
            historicReadingValue: lastReadingEvent?.readingValue,
            unit: lastReadingEvent != null ? unit : undefined,
            historicReadingDate: lastReadingEvent?.readingDate != null ? dayjs(lastReadingEvent.readingDate).format('DD.MM.YYYY') : undefined,
        },
    };
};

export interface ConfirmValidationSummary {
    perTask: Record<string, ConfirmTaskValidation>;
    // Product decision (2026-07-09): the assistant's recognition photos satisfy the image
    // requirements collectively — ANY collected image counts (the manual flow gates per-task
    // uploads, but confirm-step edits cannot attach photos, so per-value gating would dead-end).
    hasAnyImage: boolean;
    // Comment counts once it has >= 5 non-whitespace characters (JobDetails.tsx:658).
    commentValid: boolean;
    imageAlwaysRequiredError: boolean;
    imageRequiredError: boolean;
    commentRequiredError: boolean;
    // Both flags active means EITHER an image or a comment satisfies the requirement — blocked
    // only when both are missing (JobDetails.tsx:555-571).
    imageOrCommentRequiredError: boolean;
    strictError: boolean;
    submitBlocked: boolean;
    // Whether the confirm stepper needs the extra reading-hint step at the end: the comment
    // requirement is active, at least one value is implausible, and the requirement is not
    // already satisfied by an image (both-flags either/or rule). Independent of the current
    // comment text on purpose — the step must not vanish while the user is typing into it.
    commentStepRequired: boolean;
}

export const validateConfirm = (
    accumulator: AccumulatorState,
    comment: string | undefined,
    features: ConfirmValidationFeatures,
): ConfirmValidationSummary => {
    const perTask: Record<string, ConfirmTaskValidation> = {};
    for (const task of accumulator.expected) {
        perTask[task.taskId] = validateConfirmTask(task, accumulator.collected[task.taskId]?.value ?? '', features);
    }
    const tasks = Object.values(perTask);
    const anyValidationError = tasks.some((v) => v.hasAnyValidationError);

    const hasAnyImage = Object.values(accumulator.collected).some((c) => !!c.imageBase64);
    const commentValid = comment !== undefined && comment.replace(/\s/g, '').length >= 5;

    const imageAlwaysRequiredError = features.imageAlwaysRequired && !hasAnyImage && tasks.some((v) => !v.isEmpty);
    const imageRequiredError = features.unplausibleReadingsImageRequired && !hasAnyImage && anyValidationError;
    // !comment mirrors JobDetails.tsx:537: a started-but-short comment is handled separately below.
    const commentRequiredError = features.unplausibleReadingsCommentRequired && !comment && !commentValid && anyValidationError;

    const imageOrCommentRequiredError =
        features.unplausibleReadingsImageRequired && features.unplausibleReadingsCommentRequired
            ? imageRequiredError && commentRequiredError
            : imageRequiredError || commentRequiredError;

    const strictError = features.strictValidation && tasks.some((v) => v.strictInvalid);
    // (comment && !commentValid): a comment that was started but is too short also blocks, exactly
    // like the manual send button (JobDetails.tsx:580).
    const submitBlocked = imageAlwaysRequiredError || imageOrCommentRequiredError || (!!comment && !commentValid) || strictError;

    const commentStepRequired =
        features.unplausibleReadingsCommentRequired &&
        anyValidationError &&
        (features.unplausibleReadingsImageRequired ? !hasAnyImage : true);

    return {
        perTask,
        hasAnyImage,
        commentValid,
        imageAlwaysRequiredError,
        imageRequiredError,
        commentRequiredError,
        imageOrCommentRequiredError,
        strictError,
        submitBlocked,
        commentStepRequired,
    };
};

// Total number of confirm stepper steps: one per expected register, plus the reading-hint step
// when required. Shared by the screen (isLast/submit routing) and the panel (rendering).
export const confirmStepCount = (accumulator: AccumulatorState, validation: ConfirmValidationSummary): number =>
    accumulator.expected.length + (validation.commentStepRequired ? 1 : 0);
